import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Container,
  Card,
  Box,
  Stack,
  Typography,
  Switch,
  Button,
  Divider,
  Chip,
  Alert,
  Snackbar,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import Page from "../components/Page";
import { useIsRootSuperAdmin } from "src/hooks/useIsRootSuperAdmin";
import dashboardService from "src/services/dashboardService";
import { alertSlice } from "src/store/alert/alert.slice";
import {
  getDashboardV2Preview,
  setDashboardV2Preview,
  clearDashboardV2Preview,
} from "src/utils/featureFlags";

// Super-admin-only settings hub. Currently exposes the feature-flag registry;
// new system settings can be added as further sections here.

// Registry of flags a super admin can preview at runtime. These are LIVE,
// browser-session-only previews — the authoritative control is the per-company
// setting (Company → Settings). The preview never persists across sessions and
// never overrides the company setting for anyone else.
const FEATURE_FLAGS = [
  {
    key: "DASHBOARD_V2",
    title: "New Dashboard (V2) — preview",
    description:
      "Preview the new dashboard for THIS browser session only. It does not change what anyone else sees. Company-wide enablement is set per company from Company → Settings (super admin).",
    read: getDashboardV2Preview,
    set: setDashboardV2Preview,
    clear: clearDashboardV2Preview,
  },
];

const AdminSettings = () => {
  const isRootSuperAdmin = useIsRootSuperAdmin();
  const dispatch = useDispatch();

  // Persistent V2 toggle for the super admin's OWN (GP) company. The super admin
  // has no Company page for their own company, so this is where they control it.
  // Backed by the same company.dashboard_v2_enabled flag the dashboard reads.
  const [companyV2, setCompanyV2] = useState(false);
  const [companyV2Loading, setCompanyV2Loading] = useState(true);
  const [companyV2Saving, setCompanyV2Saving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      const config = await dashboardService.getDashboardConfig();
      if (active) {
        setCompanyV2(config?.dashboardV2Enabled === true);
        setCompanyV2Loading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleCompanyV2Toggle = async (event) => {
    const next = event.target.checked;
    const previous = companyV2;
    setCompanyV2(next); // optimistic
    setCompanyV2Saving(true);
    try {
      const result = await dashboardService.updateOwnDashboardConfig(next);
      setCompanyV2(result?.dashboardV2Enabled === true);
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message: `New Dashboard (V2) ${
            result?.dashboardV2Enabled ? "enabled" : "disabled"
          } for your console. Reload to apply.`,
          severity: "success",
        })
      );
    } catch (error) {
      setCompanyV2(previous); // roll back
      dispatch(
        alertSlice.actions.setAlert({
          open: true,
          message:
            error?.response?.data?.message || "Failed to update dashboard setting",
          severity: "error",
        })
      );
    } finally {
      setCompanyV2Saving(false);
    }
  };

  // Track previews in state so the switches update immediately; the actual
  // routing change applies after a reload (we prompt for it). A value of null
  // means "no preview set — defer to the company setting".
  const [previews, setPreviews] = useState(() =>
    FEATURE_FLAGS.reduce((acc, flag) => {
      acc[flag.key] = flag.read();
      return acc;
    }, {})
  );
  const [snackbar, setSnackbar] = useState(null);

  const effectiveState = useMemo(
    () =>
      FEATURE_FLAGS.reduce((acc, flag) => {
        // For display: a null preview shows as off (the company setting decides
        // at runtime), an explicit preview shows its value.
        acc[flag.key] = previews[flag.key] === true;
        return acc;
      }, {}),
    [previews]
  );

  // Only the root super admin may see this page.
  if (!isRootSuperAdmin) {
    return <Navigate to="/pagenotfound" replace />;
  }

  const setFlag = (flag, enabled) => {
    flag.set(enabled);
    setPreviews((prev) => ({ ...prev, [flag.key]: enabled }));
    setSnackbar(
      `"${flag.title}" ${enabled ? "enabled" : "disabled"} for this session. Reload to apply.`
    );
  };

  const resetFlag = (flag) => {
    flag.clear();
    setPreviews((prev) => ({ ...prev, [flag.key]: null }));
    setSnackbar(
      `"${flag.title}" preview cleared — the company setting now applies. Reload to apply.`
    );
  };

  return (
    <Page title="System Settings">
      <Container maxWidth="lg">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            System Settings
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            Super-admin configuration. The Dashboard setting is saved for your
            console; the Feature Flags below are per-browser-session previews.
          </Typography>
        </Box>

        <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Enable the new analytics dashboard (InsightHub V2) for your
            super-admin console. This is saved and applies on reload.
          </Typography>
          {companyV2Loading ? (
            <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            </Box>
          ) : (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={1.5}
              sx={{ py: 1 }}
            >
              <Box sx={{ pr: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  New Dashboard (V2)
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  When on, your console shows the new analytics dashboard instead
                  of the classic one.
                </Typography>
              </Box>
              <FormControlLabel
                sx={{ m: 0, flexShrink: 0 }}
                control={
                  <Switch
                    checked={companyV2}
                    onChange={handleCompanyV2Toggle}
                    disabled={companyV2Saving}
                  />
                }
                label={companyV2 ? "Enabled" : "Disabled"}
                labelPlacement="start"
              />
            </Stack>
          )}
          <Alert severity="info" sx={{ mt: 2 }}>
            After changing this, reload the app to apply it.
            <Button
              size="small"
              sx={{ ml: 1 }}
              onClick={() => window.location.reload()}
            >
              Reload now
            </Button>
          </Alert>
        </Card>

        <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            Feature Flags
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
            Toggle experimental features. A reload is required for routing changes
            to take effect.
          </Typography>

          <Stack divider={<Divider />} spacing={0}>
            {FEATURE_FLAGS.map((flag) => {
              const enabled = effectiveState[flag.key];
              const preview = previews[flag.key];
              const usingDefault = preview === null || preview === undefined;
              return (
                <Box key={flag.key} sx={{ py: 2 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1.5}
                  >
                    <Box sx={{ pr: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {flag.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={usingDefault ? "Company setting" : "Session preview"}
                          color={usingDefault ? "default" : "warning"}
                          variant={usingDefault ? "outlined" : "filled"}
                        />
                      </Stack>
                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                        {flag.description}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Switch
                        checked={enabled}
                        onChange={(e) => setFlag(flag, e.target.checked)}
                        inputProps={{ "aria-label": `toggle ${flag.title}` }}
                      />
                      {!usingDefault && (
                        <Button size="small" onClick={() => resetFlag(flag)}>
                          Reset
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Stack>

          <Alert severity="info" sx={{ mt: 2 }}>
            After changing a flag, reload the app to apply it.
            <Button
              size="small"
              sx={{ ml: 1 }}
              onClick={() => window.location.reload()}
            >
              Reload now
            </Button>
          </Alert>
        </Card>

        <Snackbar
          open={Boolean(snackbar)}
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
          message={snackbar || ""}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Container>
    </Page>
  );
};

export default AdminSettings;
